/// <reference types="google.maps" />
import { buildLanguage, languageFor } from '../../common/formatting-locale';
import { GeoPosition } from '../../common/Geo';
import { GoogleMapConfig, MapAdapter, MapHandle, MapOptions, MapPin, MapViewport } from './map-adapter';

type GoogleMaps = typeof google.maps;
type PinMarker = google.maps.marker.AdvancedMarkerElement | google.maps.Marker;

/** The global the API calls once its script has loaded. */
const READY_CALLBACK = '__getlocalGoogleMapsReady';

/**
 * How long after a programmatic move its `idle` may still arrive. Google animates a pan or a
 * `fitBounds` for a few hundred milliseconds; an `idle` inside this window is the handle's own.
 */
const PROGRAMMATIC_MOVE_MS = 800;

/** A private seller's coordinate is a postcode centroid about a kilometre wide (backend decision 5). */
const POSTAL_CODE_AREA_METRES = 1000;

/** Placeholders, like the Leaflet stylesheet's: GetLocal has no brand colour yet. */
const PIN_COLOUR = '#0d6efd';
const AREA_COLOUR = '#6f42c1';
const ORIGIN_COLOUR = '#dc3545';

/**
 * #172 (decided 2026-10-21): the Google Maps JavaScript API behind the {@link MapAdapter} seam —
 * the default provider, because its free tier covers a marketplace's first months and the
 * native SDK is free on Android. Leaflet + MapTiler stays the configured fallback.
 *
 * The script is injected the first time a map is created, never from index.html (#201), with
 * the key and the page's language, and nothing of Google's loads on a page without a map. A
 * map load is billed per map INSTANCE, which is why the component keeps a collapsed map alive
 * rather than re-creating it. Pins are `AdvancedMarkerElement`s with the same DOM and classes
 * the Leaflet implementation uses when a Map ID is configured, and classic symbol markers
 * without one (advanced markers need a Map ID — it is free, but it is one more console step).
 */
export class GoogleMapAdapter implements MapAdapter {
  private library: Promise<GoogleMaps> | null = null;

  async create(container: HTMLElement, options: MapOptions): Promise<MapHandle> {
    const maps = await this.load(options.config.google);
    await Promise.all([maps.importLibrary('maps'), maps.importLibrary('marker')]);
    return new GoogleMapHandle(maps, container, options);
  }

  private load(config: GoogleMapConfig): Promise<GoogleMaps> {
    this.library ??= loadGoogleMaps(config.apiKey);
    return this.library;
  }
}

/** Resolves to the loaded namespace — the one already on the page, or the script's once it ran. */
function loadGoogleMaps(apiKey: string): Promise<GoogleMaps> {
  const page = globalThis as { google?: { maps?: GoogleMaps } } & Record<string, unknown>;
  if (page.google?.maps?.Map) {
    return Promise.resolve(page.google.maps);
  }
  return new Promise((resolve, reject) => {
    page[READY_CALLBACK] = () => {
      delete page[READY_CALLBACK];
      resolve(page.google.maps);
    };
    const params = new URLSearchParams({
      key: apiKey,
      v: 'weekly',
      loading: 'async',
      libraries: 'marker',
      language: languageFor(buildLanguage()),
      region: 'DE',
      callback: READY_CALLBACK,
    });
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.onerror = () => {
      delete page[READY_CALLBACK];
      reject(new Error('The Google Maps JavaScript API could not be loaded.'));
    };
    document.head.appendChild(script);
  });
}

class GoogleMapHandle implements MapHandle {
  private readonly map: google.maps.Map;
  private readonly infoWindow: google.maps.InfoWindow;
  private readonly useAdvancedMarkers: boolean;
  private pins: { marker: PinMarker; circle: google.maps.Circle | null }[] = [];
  private pinPositions: google.maps.LatLngLiteral[] = [];
  private originMarker: PinMarker | null = null;
  private suppressUntil = 0;

  constructor(
    private readonly maps: GoogleMaps,
    private readonly container: HTMLElement,
    private readonly options: MapOptions,
  ) {
    const mapId = options.config.google.mapId?.trim();
    this.useAdvancedMarkers = !!mapId;
    // The first `idle` is the map's own arrival, not a gesture.
    this.suppress();
    this.map = new maps.Map(container, {
      center: toLatLng(options.center),
      zoom: options.zoom,
      ...(mapId ? { mapId } : {}),
      // Ctrl/⌘ + wheel zooms, a plain wheel scrolls the page past the map — Google's own answer
      // to an embedded map that must not swallow the page's scrolling.
      gestureHandling: 'cooperative',
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      clickableIcons: false,
    });
    this.infoWindow = new maps.InfoWindow();
    this.map.addListener('idle', () => {
      if (Date.now() < this.suppressUntil) {
        return;
      }
      options.onViewportChange(this.viewport());
    });
  }

  setPins(pins: readonly MapPin[]): void {
    this.clearPins();
    for (const pin of pins) {
      const position = toLatLng(pin.position);
      this.pinPositions.push(position);
      const isArea = pin.precision === 'postalCode';
      const circle = isArea
        ? new this.maps.Circle({
            map: this.map,
            center: position,
            radius: POSTAL_CODE_AREA_METRES,
            strokeColor: AREA_COLOUR,
            strokeWeight: 1,
            fillColor: AREA_COLOUR,
            fillOpacity: 0.12,
            clickable: false,
          })
        : null;
      const marker = this.createMarker(position, pin.title, {
        label: String(Math.max(0, Math.trunc(pin.count))),
        className: isArea ? 'catalog-map-pin catalog-map-pin--area' : 'catalog-map-pin',
        colour: isArea ? AREA_COLOUR : PIN_COLOUR,
        size: 30,
      });
      marker.addListener('click', () => this.openPopup(marker, pin));
      this.pins.push({ marker, circle });
    }
  }

  setOrigin(position: GeoPosition | null, label: string): void {
    if (this.originMarker) {
      removeMarker(this.originMarker);
      this.originMarker = null;
    }
    if (!position) {
      return;
    }
    this.originMarker = this.createMarker(toLatLng(position), label, {
      label: '',
      className: 'catalog-map-origin-marker',
      colour: ORIGIN_COLOUR,
      size: 16,
    });
  }

  fitRadius(center: GeoPosition, radiusKm: number): void {
    // A circle that is on no map still knows its bounds — the box the radius needs.
    const bounds = new this.maps.Circle({ center: toLatLng(center), radius: radiusKm * 1000 }).getBounds();
    if (!bounds) {
      return;
    }
    this.suppress();
    this.map.fitBounds(bounds, 12);
  }

  fitPins(): void {
    if (!this.pinPositions.length) {
      return;
    }
    this.suppress();
    if (this.pinPositions.length === 1) {
      this.map.setCenter(this.pinPositions[0]);
      this.map.setZoom(13);
      return;
    }
    const bounds = new this.maps.LatLngBounds();
    for (const position of this.pinPositions) {
      bounds.extend(position);
    }
    this.map.fitBounds(bounds, 24);
  }

  setView(center: GeoPosition, zoom: number): void {
    this.suppress();
    this.map.setCenter(toLatLng(center));
    this.map.setZoom(zoom);
  }

  viewport(): MapViewport {
    const zoom = this.map.getZoom() ?? 0;
    const bounds = this.map.getBounds();
    if (!bounds) {
      // Before the first render the map has no bounds; the centre with no extent is honest.
      const centre = this.map.getCenter();
      const latitude = centre?.lat() ?? 0;
      const longitude = centre?.lng() ?? 0;
      return { north: latitude, south: latitude, east: longitude, west: longitude, zoom };
    }
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    return {
      north: clamp(northEast.lat(), -90, 90),
      south: clamp(southWest.lat(), -90, 90),
      east: clamp(northEast.lng(), -180, 180),
      west: clamp(southWest.lng(), -180, 180),
      zoom,
    };
  }

  invalidateSize(): void {
    this.suppress();
    this.maps.event.trigger(this.map, 'resize');
  }

  destroy(): void {
    this.clearPins();
    this.setOrigin(null, '');
    this.infoWindow.close();
    this.maps.event.clearInstanceListeners(this.map);
    // The API has no destroy: dropping every reference and emptying the container is what
    // releases a map, and the container is reused by the next create.
    this.container.replaceChildren();
  }

  private createMarker(
    position: google.maps.LatLngLiteral,
    title: string,
    style: { label: string; className: string; colour: string; size: number },
  ): PinMarker {
    if (this.useAdvancedMarkers) {
      // The same DOM and classes the Leaflet implementation draws, so both providers look alike.
      // Advanced markers anchor their content's bottom centre at the position; the circle's
      // centre belongs there, hence the half-height shift.
      const content = document.createElement('div');
      content.className = style.className;
      content.style.width = `${style.size}px`;
      content.style.height = `${style.size}px`;
      content.style.transform = 'translateY(50%)';
      content.textContent = style.label;
      return new this.maps.marker.AdvancedMarkerElement({ map: this.map, position, title, content });
    }
    return new this.maps.Marker({
      map: this.map,
      position,
      title,
      label: style.label ? { text: style.label, color: '#fff', fontWeight: '600', fontSize: '12px' } : undefined,
      icon: {
        path: this.maps.SymbolPath.CIRCLE,
        scale: style.size / 2,
        fillColor: style.colour,
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
      },
    });
  }

  private openPopup(anchor: PinMarker, pin: MapPin): void {
    this.infoWindow.setContent(this.options.popupContent(pin));
    this.infoWindow.open({ map: this.map, anchor });
  }

  private clearPins(): void {
    for (const { marker, circle } of this.pins) {
      removeMarker(marker);
      circle?.setMap(null);
    }
    this.pins = [];
    this.pinPositions = [];
  }

  private suppress(): void {
    this.suppressUntil = Date.now() + PROGRAMMATIC_MOVE_MS;
  }
}

function removeMarker(marker: PinMarker): void {
  if ('setMap' in marker) {
    marker.setMap(null);
  } else {
    marker.map = null;
  }
}

function toLatLng(position: GeoPosition): google.maps.LatLngLiteral {
  return { lat: position.latitude, lng: position.longitude };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
